// ErrorBoundary.tsx -- Top-level React error boundary (bug-137).
// A single uncaught render throw used to unmount the whole tree and white-screen
// the SPA. This contains the blast radius to a styled fallback with a reload
// affordance, reading the app's themeable CSS tokens so it matches both themes.

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Shown above the error message; defaults to a generic surface label. */
  label?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', this.props.label ?? '', error, info.componentStack);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const label = this.props.label ?? 'This surface';
    const message = this.state.error?.message ?? 'An unexpected error occurred.';

    return (
      <div
        className="v2-content"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          gap: 14,
          textAlign: 'center',
        }}
      >
        <span
          style={{
            color: 'var(--rose)',
            fontSize: 14,
            fontFamily: 'var(--mono)',
            letterSpacing: '0.08em',
          }}
        >
          {label} -- Something went wrong
        </span>
        <span
          style={{
            color: 'var(--text3)',
            fontSize: 12,
            fontFamily: 'var(--sans)',
            maxWidth: 420,
            lineHeight: 1.6,
          }}
        >
          {message}
        </span>
        <button
          type="button"
          className="v2-btn"
          onClick={this.handleReload}
          style={{ marginTop: 4 }}
        >
          RELOAD
        </button>
      </div>
    );
  }
}
